import React, { useState, useMemo } from 'react';

/**
 * Transit Table — Planet | Sign | Nakshatra | Pada | House from Lagna | House from Moon | flags
 *
 * Props:
 *   - transits: data.transits object
 *   - onClickRow: (planetState) => void
 *   - filter: { all, slow, fast, retro, combust }
 */

const SLOW_PLANETS = new Set(['Jupiter', 'Saturn', 'Rahu', 'Ketu']);
const FAST_PLANETS = new Set(['Sun', 'Moon', 'Mars', 'Mercury', 'Venus']);

export default function TransitTable({ transits, onClickRow, filter = 'all' }) {
  const lagnaRows = transits?.from_lagna || [];
  const moonRows = transits?.from_moon || [];

  const rows = useMemo(() => {
    if (!transits || !transits.current) return [];
    return transits.current.map((p, i) => ({
      ...p,
      house_from_lagna: lagnaRows[i]?.house,
      house_from_moon: moonRows[i]?.house,
    })).filter(p => {
      if (filter === 'slow') return SLOW_PLANETS.has(p.name_en);
      if (filter === 'fast') return FAST_PLANETS.has(p.name_en);
      if (filter === 'retro') return p.is_retrograde;
      if (filter === 'combust') return p.is_combust;
      return true;
    });
  }, [transits, lagnaRows, moonRows, filter]);

  if (!transits || !transits.current) return null;

  return (
    <div data-testid="transit-table" className="overflow-x-auto rounded-xl border border-gold/20">
      <table className="w-full text-sm">
        <thead className="text-xs uppercase tracking-wider text-gold/80 bg-purple-950/40">
          <tr>
            <th className="text-left py-2 px-3">ग्रह</th>
            <th className="text-left py-2 px-3">राशि</th>
            <th className="text-left py-2 px-3">अंश</th>
            <th className="text-left py-2 px-3">नक्षत्र</th>
            <th className="text-center py-2 px-3">पाद</th>
            <th className="text-center py-2 px-3">लग्न से भाव</th>
            <th className="text-center py-2 px-3">चंद्र से भाव</th>
            <th className="text-center py-2 px-3">स्थिति</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr><td colSpan={8} className="py-4 text-center text-text-muted">कोई ग्रह नहीं मिले इस फ़िल्टर में</td></tr>
          )}
          {rows.map((p, i) => {
            const marks = [];
            if (p.is_retrograde) marks.push(<Mark key="r" t="↺" c="bg-amber-500/15 text-amber-300 border-amber-400/40" />);
            if (p.is_combust) marks.push(<Mark key="c" t="🔥" c="bg-red-500/15 text-red-300 border-red-400/40" />);
            if (p.is_exalted) marks.push(<Mark key="e" t="↑" c="bg-emerald-500/15 text-emerald-300 border-emerald-400/40" />);
            if (p.is_debilitated) marks.push(<Mark key="d" t="↓" c="bg-rose-500/15 text-rose-300 border-rose-400/40" />);
            if (p.is_own_sign) marks.push(<Mark key="o" t="⚜" c="bg-gold/15 text-gold border-gold/40" />);
            return (
              <tr
                key={p.name_en}
                onClick={() => onClickRow && onClickRow(p)}
                data-testid={`transit-row-${p.name_en}`}
                className="border-t border-white/5 hover:bg-white/5 cursor-pointer"
              >
                <td className="py-2 px-3 font-semibold text-gold">{p.name_hi}</td>
                <td className="py-2 px-3">{p.sign_hi}</td>
                <td className="py-2 px-3 font-mono text-xs">{p.dms}</td>
                <td className="py-2 px-3">{p.nakshatra_hi}</td>
                <td className="py-2 px-3 text-center">{p.pada}</td>
                <td className="py-2 px-3 text-center font-mono">{p.house_from_lagna ?? '—'}</td>
                <td className="py-2 px-3 text-center font-mono">{p.house_from_moon ?? '—'}</td>
                <td className="py-2 px-3">
                  <div className="flex gap-1 justify-center flex-wrap">{marks.length === 0 ? <span className="text-text-muted">—</span> : marks}</div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

const Mark = ({ t, c }) => <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-semibold border ${c}`}>{t}</span>;
